import { Slot } from '@radix-ui/react-slot';
import mergeRefs from 'merge-refs';
import React, {
  forwardRef,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { cn } from 'v5/platform/dom/cn';
import { useResizable } from 'v5/platform/react/useResizable';
import styles from './DockableLayout.module.scss';
import {
  DockableLayoutContext,
  DockMode,
  useDockableLayout
} from './useDockableLayout';

// Transition styles - single source of truth for all animations
export const SIDEBAR_TRANSITION_STYLE = '300ms ease-in-out'; // For width changes and docking
export const SIDEBAR_SLIDE_TRANSITION_STYLE = '200ms ease-in-out'; // For slide in/out (faster)

// CSS @property declarations for animatable CSS variables
const cssPropertyDeclarations = `
  @property --sidebar-width {
    syntax: '<length>';
    initial-value: 0px;
    inherits: true;
  }
  
  @property --sidebar-effective-width {
    syntax: '<length>';
    initial-value: 0px;
    inherits: true;
  }
  
  @property --sidebar-translate-x {
    syntax: '<percentage>';
    initial-value: 0%;
    inherits: true;
  }

  @property --header-height {
    syntax: '<length>';
    initial-value: 0px;
    inherits: true;
  }
`;

// DockableHeader - Headless header wrapper that measures height
export interface DockableHeaderProps {
  /**
   * Single child element to wrap
   */
  children: ReactElement;
}

export const DockableHeader = forwardRef<HTMLElement, DockableHeaderProps>(
  ({ children }, forwardedRef) => {
    const localRef = useRef<HTMLElement>(null);
    const context = useContext(DockableLayoutContext);

    useEffect(() => {
      const element = localRef.current;
      if (!element || !context) return;

      // Function to measure and update height
      const updateHeight = () => {
        // Directly update the CSS variable on the root element
        const root = element.closest('[data-dockable-layout]');
        if (root instanceof HTMLElement) {
          root.style.setProperty(
            '--header-height',
            `${element.offsetHeight}px`
          );
        }
      };

      // Initial measurement
      updateHeight();

      // Watch for size changes
      const resizeObserver = new ResizeObserver(() => {
        updateHeight();
      });

      resizeObserver.observe(element);

      return () => {
        resizeObserver.disconnect();
      };
    }, [context]);

    return <Slot ref={mergeRefs(localRef, forwardedRef)}>{children}</Slot>;
  }
);
DockableHeader.displayName = 'DockableHeader';

// DockableToggle - Headless button wrapper with transition protection
export interface DockableToggleProps {
  /**
   * Single child element to wrap
   */
  children: ReactElement;
  openOnHover?: boolean;
}

export const DockableToggle = forwardRef<HTMLElement, DockableToggleProps>(
  ({ children, openOnHover = true }, ref) => {
    const context = useDockableLayout();
    if (!context) return null;
    const { isTransitioning, setMode, mode, isHovered, setIsHovered } = context;

    return (
      <Slot
        ref={ref}
        onClick={() => {
          if (isTransitioning) return;
          setMode(mode === 'docked' ? 'collapsed' : 'docked');
        }}
        onMouseOver={() => {
          if (isTransitioning || isHovered || !openOnHover) return;
          return setIsHovered(true);
        }}
      >
        {children}
      </Slot>
    );
  }
);
DockableToggle.displayName = 'DockableToggle';

export interface DockableSidebarToggleProps {
  children: ReactElement;
}

export const DockableSidebarToggle = forwardRef<
  HTMLElement,
  DockableToggleProps
>(({ children }, ref) => {
  const context = useDockableLayout();
  if (!context) return null;
  const { setMode, mode, setIsHovered } = context;

  return (
    <Slot
      ref={ref}
      data-dockable-sidebar-toggle-mode={mode}
      onClick={() => {
        setIsHovered(true);
        setMode(mode === 'docked' ? 'collapsed' : 'docked');
      }}
    >
      {children}
    </Slot>
  );
});
DockableSidebarToggle.displayName = 'DockableSidebarToggle';

export interface DockableLayoutProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Content to show in the dockable panel (left side)
   * Receives mode, width, isHovered, isTransitioning props
   */
  panel:
    | ((props: {
        mode: DockMode;
        width: number;
        isHovered: boolean;
        onHoverChange: (hovered: boolean) => void;
        isTransitioning: boolean;
      }) => ReactNode)
    | ReactNode;

  /**
   * Main content area
   */
  children: ReactNode;

  /** Class for the `main` element that holds `children`. */
  mainClassName?: string;

  /**
   * Current dock mode (controlled)
   */
  mode?: DockMode;

  /**
   * Mode change callback
   */
  onModeChange?: (mode: DockMode) => void;

  /**
   * Initial/controlled width
   */
  width?: number;

  /**
   * Width change callback
   */
  onWidthChange?: (width: number) => void;

  /**
   * Minimum panel width
   */
  minWidth?: number;

  /**
   * Maximum panel width
   */
  maxWidth?: number;

  /**
   * Enable drag-to-resize
   */
  resizable?: boolean;

  /**
   * Hover detection zone width (left edge)
   */
  edgeDetectorWidth?: number;

  /**
   * Custom resize handle renderer
   */
  renderResizeHandle?: (props: {
    onMouseDown: (e: React.MouseEvent) => void;
    onClick: (e: React.MouseEvent) => void;
    isResizing: boolean;
  }) => ReactNode;
}

/**
 * Headless dockable layout component
 * Handles docked/floating states, resizing, and CSS variable animations
 * Content-agnostic - works with any panel content
 */
export const DockableLayout = forwardRef<HTMLDivElement, DockableLayoutProps>(
  (
    {
      panel,
      children,
      mode: controlledMode,
      onModeChange,
      width: controlledWidth,
      onWidthChange,
      minWidth = 220,
      maxWidth = 480,
      resizable = true,
      edgeDetectorWidth = 8,
      renderResizeHandle,
      className,
      mainClassName,
      ...props
    },
    ref
  ) => {
    const hasPanel = !!panel;
    const [uncontrolledMode, setUncontrolledMode] =
      useState<DockMode>('docked');
    const [isHovered, setIsHovered] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const prevModeRef = React.useRef<DockMode>(uncontrolledMode);

    const mode = controlledMode ?? uncontrolledMode;
    const setMode = onModeChange ?? setUncontrolledMode;

    // Handle mode transitions
    React.useEffect(() => {
      const prevMode = prevModeRef.current;
      prevModeRef.current = mode;

      if (mode === 'docked') {
        // Clear hover when docking
        setIsHovered(false);
        setIsTransitioning(true);
        // Clear transitioning after animation completes (300ms)
        const timeout = setTimeout(() => setIsTransitioning(false), 300);
        return () => clearTimeout(timeout);
      } else if (prevMode === 'docked' && mode === 'collapsed') {
        // Hide panel when collapsing
        setIsHovered(false);
        setIsTransitioning(true);
        // Clear transitioning after animation completes (300ms)
        const timeout = setTimeout(() => setIsTransitioning(false), 300);
        return () => clearTimeout(timeout);
      }
    }, [mode]);

    const {
      width,
      isResizing,
      handleMouseDown: handleResizeMouseDown,
      didDrag
    } = useResizable({
      initialWidth: controlledWidth ?? 256,
      minWidth,
      maxWidth,
      onWidthChange
    });

    const panelWidth = hasPanel ? (controlledWidth ?? width) : 0;
    const isResizableEnabled = hasPanel && resizable;

    const handleResizeClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // Only collapse if we didn't just finish dragging
      if (!didDrag()) {
        setMode('collapsed');
      }
    };

    return (
      <DockableLayoutContext.Provider
        value={{
          // State
          mode,
          width: panelWidth,
          isHovered,
          isTransitioning,
          isResizing,
          // Handlers
          setMode,
          setWidth: onWidthChange ?? (() => {}),
          setIsHovered
        }}
      >
        {/* Inject CSS @property declarations for animatable variables */}
        <style>{cssPropertyDeclarations}</style>

        <div
          ref={ref}
          className={cn(styles.layoutRoot, className)}
          data-dockable-layout="true"
          data-sidebar-transitioning={isTransitioning ? '1' : '0'}
          data-sidebar-mode={mode}
          data-sidebar-floating-hovered={
            mode === 'collapsed' ? isHovered : undefined
          }
          style={
            {
              // Expose dimensions as CSS variables
              '--sidebar-width': `${panelWidth}px`,
              '--sidebar-effective-width':
                mode === 'docked' ? `${panelWidth}px` : '0px',
              '--sidebar-translate-x':
                mode === 'collapsed' && !isHovered ? '-100%' : '0%',
              '--is-transitioning': isTransitioning ? '1' : '0',
              // Transition the CSS variables (disabled during resize)
              transition: isResizing
                ? 'none'
                : `--sidebar-width ${SIDEBAR_TRANSITION_STYLE}, --sidebar-effective-width ${SIDEBAR_TRANSITION_STYLE}, --sidebar-translate-x ${SIDEBAR_SLIDE_TRANSITION_STYLE}`,
              ...props.style
            } as React.CSSProperties
          }
          {...props}
        >
          {/* Panel Container - fixed position, styles in SCSS */}
          <div
            className={cn(
              styles.panelContainer,
              !isHovered && mode === 'collapsed' && styles.noPointerEvents
            )}
            style={{
              top: mode === 'docked' ? 0 : 'var(--header-height)',
              width: `${panelWidth}px`,
              transition: `top ${SIDEBAR_TRANSITION_STYLE}`
            }}
          >
            {/* Panel content with animated transform */}
            {panel && (
              <div
                className={cn(
                  'tailwind-root', // for children to work
                  styles.panelContent,
                  // Always enable pointer events when docked, or when collapsed+hovered
                  (mode === 'docked' || (mode === 'collapsed' && isHovered)) &&
                    styles.enablePointerEvents
                )}
                style={{
                  transform: 'translateX(var(--sidebar-translate-x))'
                }}
                onMouseEnter={() => {
                  setIsHovered(true);
                }}
                onMouseLeave={() => {
                  setIsHovered(false);
                }}
              >
                {typeof panel === 'function'
                  ? panel({
                      mode,
                      width: panelWidth,
                      isHovered,
                      onHoverChange: setIsHovered,
                      isTransitioning
                    })
                  : panel}
              </div>
            )}

            {/* Resize Handle */}
            {isResizableEnabled && mode === 'docked' && (
              <>
                {renderResizeHandle ? (
                  renderResizeHandle({
                    onMouseDown: handleResizeMouseDown,
                    onClick: handleResizeClick,
                    isResizing
                  })
                ) : (
                  <div
                    className={cn(
                      styles.resizeHandle,
                      isResizing && styles.isResizing
                    )}
                    onMouseDown={handleResizeMouseDown}
                    onClick={handleResizeClick}
                    role="separator"
                    aria-label="Resize panel"
                  />
                )}
              </>
            )}
          </div>

          {/* Edge Detector - for hover detection when collapsed */}
          {hasPanel && mode === 'collapsed' && !isHovered && (
            <div
              className={styles.edgeDetector}
              style={{
                top: 'var(--header-height)',
                width: `${edgeDetectorWidth}px`
              }}
              onMouseEnter={() => setIsHovered(true)}
            />
          )}

          {/* Main Content Area - uses animated CSS variable */}
          {/* Note: Not wrapped in tailwind-root for legacy page compatibility */}
          <main className={cn(styles.mainContent, mainClassName)}>
            {children}
          </main>
        </div>
      </DockableLayoutContext.Provider>
    );
  }
);
DockableLayout.displayName = 'DockableLayout';
